import { useState } from 'react';
import type { SavedAddress } from '../../types/address';

interface AddressCardProps {
  address: SavedAddress;
  onDelete: (id: number) => Promise<void>;
  isNew?: boolean;
}

export function AddressCard({ address, onDelete, isNew = false }: AddressCardProps) {
  const [isDeleting, setIsDeleting] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);

  const handleDelete = async () => {
    setIsDeleting(true);
    try {
      await onDelete(address.id);
    } catch {
      setIsDeleting(false);
      setShowConfirm(false);
    }
  };

  const isCountry = address.is_country;

  return (
    <article
      className={`relative flex h-full flex-col rounded-xl border-2 bg-white p-4 transition-all sm:p-5 ${
        isNew
          ? 'border-green-500 shadow-[0_4px_12px_rgba(92,184,92,0.2)]'
          : 'border-gray-200 hover:border-gray-300 hover:shadow-md'
      } ${isDeleting ? 'pointer-events-none opacity-50' : ''}`}
      aria-label={`Direccion: ${address.address}`}
    >
      {isNew && (
        <span className="absolute -right-2.5 -top-2.5 rounded-full bg-green-500 px-2.5 py-1 text-xs font-bold text-white">
          Nueva
        </span>
      )}

      <div className="flex flex-1 items-start gap-3">
        {/* Icon */}
        <div className={`flex h-10 w-10 shrink-0 items-center justify-center rounded-full sm:h-12 sm:w-12 ${
          isCountry ? 'bg-green-200 text-green-600' : 'bg-green-100 text-green-500'
        }`}>
          {isCountry ? (
            <svg className="h-5 w-5 sm:h-6 sm:w-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6" />
            </svg>
          ) : (
            <svg className="h-5 w-5 sm:h-6 sm:w-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
            </svg>
          )}
        </div>

        <div className="min-w-0 flex-1">
          <p className="m-0 mb-1 text-[11px] font-semibold uppercase tracking-wide text-gray-400 sm:text-xs">
            {isCountry ? 'Barrio Cerrado' : 'Direccion'}
          </p>
          <h3 className="m-0 break-words text-[15px] font-semibold leading-snug text-gray-800 sm:text-base" title={address.address}>
            {address.address}
          </h3>
          {address.suite && (
            <p className="m-0 mt-1.5 text-sm text-gray-600">
              <span className="font-medium">Depto:</span> {address.suite}
            </p>
          )}
          {isCountry && address.block && address.house && (
            <p className="m-0 mt-1.5 text-sm text-gray-600">
              <span className="font-medium">Mzna:</span> {address.block} | <span className="font-medium">Lote:</span> {address.house}
            </p>
          )}
          {address.notes && (
            <p className="m-0 mt-2 break-words text-[13px] italic text-gray-500">
              "{address.notes}"
            </p>
          )}
        </div>
      </div>

      {/* Delete */}
      <div className="mt-3 border-t border-gray-100 pt-3 sm:mt-4 sm:pt-4">
        {showConfirm ? (
          <div className="flex items-center gap-2">
            <span className="flex-1 text-sm text-gray-600">Eliminar?</span>
            <button
              type="button"
              onClick={() => setShowConfirm(false)}
              disabled={isDeleting}
              className="rounded-md border border-gray-300 bg-white px-3 py-1.5 text-sm font-medium text-gray-700 hover:bg-gray-50"
            >
              No
            </button>
            <button
              type="button"
              onClick={handleDelete}
              disabled={isDeleting}
              className="rounded-md border border-red-500 bg-red-500 px-3 py-1.5 text-sm font-medium text-white hover:bg-red-600"
            >
              {isDeleting ? 'Eliminando...' : 'Si'}
            </button>
          </div>
        ) : (
          <button
            type="button"
            onClick={() => setShowConfirm(true)}
            className="flex w-full items-center justify-center gap-2 rounded-md border border-red-400 bg-transparent px-4 py-2.5 text-sm font-medium text-red-500 transition-colors hover:bg-red-50"
            aria-label={`Eliminar direccion ${address.address}`}
          >
            <i className="fa fa-trash" aria-hidden="true"></i>
            <span>Eliminar</span>
          </button>
        )}
      </div>
    </article>
  );
}
